/**
 * CSV export for the Command Center championship universe.
 *
 * Three rules, because this is what Brian actually asked for:
 *
 *  1. IT EXPORTS THE FULL FILTERED SET. The views paginate for the DOM's sake
 *     ("show 500 more"); the export never does. Whatever the active filters +
 *     date range resolve to — thousands of rows — is what lands in the file.
 *  2. IT RESPECTS THE ACTIVE SCOPE. The caller hands in the already-filtered
 *     list (sport / level / state / owner / incumbent / year / search), so the
 *     file always matches what the operator is looking at.
 *  3. THE DATE RANGE IS ON THE EFFECTIVE DATE. Same date the whole Command
 *     Center windows on (`effDate` — confirmed, else event, else projected,
 *     else estimated from season + sport), so an export can never disagree with
 *     the board it came from.
 *
 * Generation and download are entirely client-side — no new backend surface.
 */

import { recordedChampionFacts, recordedChampionText } from './recordedChampionFacts.js'
import {coverageExportText,schoolTeamOverlay} from './schoolTeamOverlay.js'
import { worklistNextWork } from '../features/command-center/pursuit/worklistNextWork.js'
import { pursuitStage, stageMeta, contentionMeta, ownerName, NO_OWNER } from './championships.js'

/* ---- column set ------------------------------------------------------ */

const ymd = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : ''

/**
 * The exported columns, in order. Every value is derived through the SAME
 * helpers the UI renders with, so the CSV can't drift from the board.
 */
export const CSV_COLUMNS = [
  { key: 'championship', label: 'Championship', get: (c) => c.name || '' },
  /* 🔴 THE ATHLETES GET THEIR OWN COLUMN. Brian, 2026-10-19: the champion of
   * record is the school, the athletes attach as references — and the render
   * rule holds "everywhere the champion appears... any export."
   *
   * `Team / School` is UNCHANGED and stays at its original key and position,
   * because under this ruling `c.champion` holds a school on both kinds of
   * title, so the column is already correct. Nothing downstream — a sheet, a
   * filter, a mail-merge template — has to change.
   *
   * What was missing is the athletes. On an individual title the export used
   * to carry the school and lose the person entirely, so the one fact the
   * outreach is built on ("Congrats on [athlete]'s [event] title") could not
   * be mail-merged at all. One column, semicolon-separated, each entry
   * "Athlete — Event", in the order the source published them.
   *
   * ⚠️ AN UNREADABLE references VALUE EXPORTS AS A VISIBLE DEFECT, never as
   * an empty cell. A blank here means "no athletes recorded"; a blank where
   * athletes exist but cannot be parsed would be a silent zero in a file
   * somebody sends to a customer. */
  /* ⚠️ A MULTI-SCHOOL ROW EXPORTS AS "MULTI-SCHOOL (n)", NEVER AS ONE SCHOOL
   * AND NEVER AS A BLANK. Writing one of several schools into a column headed
   * Team / School is the forbidden guess leaving the building in a file
   * somebody mail-merges; a blank would read as "no champion found". Both are
   * wrong in ways the recipient cannot detect, so the cell says what it is and
   * the athletes column carries each school with its own athletes under it. */
  { key: 'team_school', label: 'Team / School',
    get: (c) => (c.multiSchool
      ? `MULTI-SCHOOL (${(c.schoolGroups || c.schools || []).length}) — account unresolved`
      : c.champion || c.winningSchool || '') },
  { key: 'athletes', label: 'Individual champions (athlete — event)',
    get: (c) => {
      if (c.athletesError) return `UNREADABLE: ${c.athletesError}`
      if (c.multiSchool && Array.isArray(c.schoolGroups)) {
        return c.schoolGroups.map((g) => `${g.school}: `
          + g.athletes.map((a) => [a.athlete, a.event].filter(Boolean).join(' — ')).join(', ')).join('; ')
      }
      return (c.athletes || []).map((a) => [a.athlete, a.event].filter(Boolean).join(' — ')).join('; ')
    } },
  { key: 'sport', label: 'Sport', get: (c) => c.sport || '' },
  { key: 'level', label: 'Level', get: (c) => c.level || '' },
  { key: 'division', label: 'Division', get: (c) => c.segment || c.division || '' },
  { key: 'state', label: 'State', get: (c) => (c.state && c.state !== 'NATIONAL' ? c.state : c.state === 'NATIONAL' ? 'National' : '') },
  { key: 'event_date', label: 'Event date (effective)', get: (c) => ymd(c.effDate) },
  { key: 'date_basis', label: 'Date basis', get: (c) => (c.effDate ? c.dateConfidence : 'none') },
  { key: 'stage', label: 'Pursuit stage / window', get: (c, now) => stageMeta(pursuitStage(c, now)).label },
  { key: 'status', label: 'Status', get: (c) => contentionMeta(c).label },
  { key: 'pursuit_status', label: 'Pursuit status', get: (c) => c.pursuit || '' },
  { key: 'incumbent', label: 'Incumbent', get: (c) => c.incumbent || '' },
  { key: 'owner', label: 'Assigned rep', get: (c) => ownerName(c) || NO_OWNER },
  { key: 'record_id', label: 'Record ID', get: (c) => c.id || '' },
]

// Append explicit winner evidence without shifting the established export keys.
// Organization/pursuit records are never given championship result columns.
export const CHAMPIONSHIP_RESULT_COLUMNS = [
  { key: 'champion', label: 'Champion', get: c => recordedChampionText(c) },
  { key: 'champion_evidence', label: 'Champion evidence', get: (c, now) => {
    const facts = recordedChampionFacts(c)
    if (!facts.applicable) return ''
    return [facts.claims.length || facts.members.length ? facts.note : '', facts.referencesError, worklistNextWork(c, now).evidence].filter(Boolean).join(' ')
  } },
  { key: 'champion_next_work', label: 'Champion next work', get: (c, now) => c.sourceType ? '' : worklistNextWork(c, now).next },
  { key: 'champion_source', label: 'Champion source', get: (c, now) => c.sourceType ? '' : worklistNextWork(c, now).source },
]

/**
 * The columns for a given set of records: the base set above, plus the
 * pursuit-space dimensions when the records actually carry them.
 *
 * APPEND-ONLY BY DESIGN. A championship export contains no record with a
 * product category, a surfaced date, a close date, an industry, a headcount or
 * a source URL, so it preserves the established base columns in their original order,
 * with the explicit championship result evidence appended. An RFP export gains
 * what an RFP needs: what the bid is FOR, when we surfaced it, when it CLOSES,
 * and where to file. A CORPORATE export gains what an employee-recognition
 * account needs: industry, total employees, HQ, pursuit level and the website.
 */
export function columnsFor(items = [], { noun = '' } = {}) {
  const championshipScope = (!noun || noun.toLowerCase() === 'championship') && (!items.length || items.some(c => !c.sourceType))
  const extra = championshipScope ? [...CHAMPIONSHIP_RESULT_COLUMNS, ...CONTACT_EXPORT_COLUMNS] : []
  const any = (fn) => items.some(fn)
  if (any(schoolTeamOverlay)) extra.push({key:'school_team_coverage',label:'School-team bowl coverage (separate from CRM assignment)',get:coverageExportText})
  if (any((c) => c.product)) {
    extra.push({ key: 'product', label: 'Product category', get: (c) => c.product || '' })
  }
  if (any((c) => c.industry)) {
    extra.push({ key: 'industry', label: 'Industry', get: (c) => c.industry || '' })
  }
  if (any((c) => c.employees != null)) {
    // The raw number, unformatted — a spreadsheet should get 4200, not "4,200".
    // Blank, never 0, when the headcount isn't on the record.
    extra.push({ key: 'employees', label: 'Total employees', get: (c) => (c.employees == null ? '' : String(c.employees)) })
  }
  if (any((c) => c.hqLocation)) {
    extra.push({ key: 'hq_location', label: 'HQ location', get: (c) => c.hqLocation || '' })
  }
  if (any((c) => c.pursuitLevel)) {
    extra.push({ key: 'pursuit_level', label: 'Pursuit level', get: (c) => c.pursuitLevel || '' })
  }
  // ORGANIZATION TYPE — "National Organization" / "Greek Chapter" / "Police
  // Department". Only present on the organization spaces that record it.
  if (any((c) => c.orgType)) {
    extra.push({ key: 'org_type', label: 'Organization type', get: (c) => c.orgType || '' })
  }
  // THE COMMUNITY HERO COLUMNS (police / fire). A department with no nomination
  // exports blank on all three — never "none", which would read as a value.
  if (any((c) => c.hero)) {
    extra.push({ key: 'hero_name', label: 'Community Hero', get: (c) => c.hero?.name || '' })
    extra.push({ key: 'hero_status', label: 'Hero status', get: (c) => c.hero?.status || '' })
    extra.push({
      key: 'hero_years',
      label: 'Years of service',
      get: (c) => (c.hero?.yearsOfService == null ? '' : String(c.hero.yearsOfService)),
    })
    extra.push({ key: 'hero_nominated', label: 'Nominated', get: (c) => ymd(c.hero?.nominatedAt) })
  }
  // THE GREEK PURSUIT DATES. The product pursued already exports through the
  // shared `product` column above; these two are the pursuit's own dates. A won
  // date only ever exists on a WON deal, so a blank here is a real "not won".
  if (any((c) => c.pursuitDate)) {
    extra.push({ key: 'pursuit_date', label: 'Pursuit date', get: (c) => ymd(c.pursuitDate) })
  }
  if (any((c) => c.wonDate)) {
    extra.push({ key: 'won_date', label: 'Won date', get: (c) => ymd(c.wonDate) })
  }
  // THE CHEER GYM COLUMNS. A partnership is BOTH halves, so the contract and
  // the exclusive jewelry program export as separate, unambiguous columns —
  // and the championship footprint (titles won by the gym's teams, and how many
  // teams) exports as the real numbers the board sorts on. Present only on
  // records that carry a partnership stage, so every other export is unchanged.
  if (any((c) => c.partnershipStage)) {
    extra.push({ key: 'partnership_stage', label: 'Partnership stage', get: (c) => c.partnershipLabel || '' })
    extra.push({ key: 'contract_signed', label: 'Contract signed', get: (c) => (c.contractSigned ? 'yes' : 'no') })
    extra.push({
      key: 'jewelry_program',
      label: 'Exclusive jewelry program',
      get: (c) => (c.jewelryLive ? 'live' : c.jewelryLabel || 'not launched'),
    })
    // Zero is a REAL answer for a gym — it is the untouched end of the TAM —
    // so it exports as 0, never as a blank.
    extra.push({ key: 'championship_titles', label: 'Championship relationships', get: (c) => String(c.titleCount ?? 0) })
    extra.push({ key: 'teams_with_titles', label: 'Teams with titles', get: (c) => String(c.teamCount ?? 0) })
    extra.push({ key: 'last_title', label: 'Last title won', get: (c) => ymd(c.lastTitleAt) })
  }
  if (any((c) => c.openDate)) {
    extra.push({ key: 'open_date', label: 'Surfaced (scan date)', get: (c) => ymd(c.openDate) })
  }
  if (any((c) => c.dateKind === 'close')) {
    // Also carried in `event_date` (the effective date every view windows on) —
    // repeated under its real name so an exported deadline is unmistakable.
    extra.push({ key: 'close_date', label: 'Close date', get: (c) => (c.dateKind === 'close' ? ymd(c.effDate) : '') })
  }
  if (any((c) => c.sourceUrl)) {
    // An ORGANIZATION's official channel is its own website — a corporation's, a
    // police or fire department's, a Greek chapter's — where a solicitation's is
    // a public listing. The column is named for whichever the export holds, and
    // it is decided from the space's OWN CTA label (`submitLabelFor` in the
    // registry) rather than by re-listing the organization kinds here, so a
    // space added later is named correctly with no edit to this file.
    const website = items.every((c) => !c.sourceUrl || /website/i.test(c.submitLabel || ''))
    extra.push({ key: 'source_url', label: website ? 'Website' : 'Official listing', get: (c) => c.sourceUrl || '' })
  }
  // The NAME column is named for the space that exported it. An RFP export
  // headed "Championship" is wrong in the file a rep actually hands to someone,
  // and the header is the one place the CSV states what a row IS. No noun (the
  // default, and every pre-existing caller) keeps the verbatim championship
  // header, while explicit result evidence is appended for championship exports.
  const base = noun
    ? [{ ...CSV_COLUMNS[0], label: `${String(noun).charAt(0).toUpperCase()}${String(noun).slice(1)}` }, ...CSV_COLUMNS.slice(1)]
    : CSV_COLUMNS
  if (!championshipScope && any(c => c.exportRecordCapturedAt)) extra.push({key:'record_snapshot_captured_at',label:'Record snapshot captured at (UTC)',get:c=>c.exportRecordCapturedAt||''})
  return extra.length ? [...base, ...extra] : base
}

/* ---- date-range scoping ---------------------------------------------- */

/** 'YYYY-MM-DD' → local Date at midnight (or null). */
export function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim())
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Scope a list to an effective-date range. `from` / `to` are 'YYYY-MM-DD'
 * strings (either may be blank = open-ended); `to` is INCLUSIVE of its day.
 * Undated records are opt-in — they belong in a "no date" export, not silently
 * in a dated one.
 */
export function scopeByDateRange(items, { from = '', to = '', includeUndated = true } = {}) {
  const start = parseYmd(from)
  const end = parseYmd(to)
  if (end) end.setHours(23, 59, 59, 999)
  if (!start && !end) return includeUndated ? items : items.filter((c) => c.effDate)
  return items.filter((c) => {
    if (!c.effDate) return includeUndated
    if (start && c.effDate < start) return false
    if (end && c.effDate > end) return false
    return true
  })
}

/* ---- serialization --------------------------------------------------- */

// Quote when the value carries a delimiter, a quote, a newline, or edge space.
function cell(value) {
  let s = value == null ? '' : String(value)
  // Neutralize spreadsheet formula injection without mangling ordinary text.
  if (/^[\s]*[=+@-]|^[\t\r]/.test(s)) s = `'${s}`
  return /[",\r\n]|^\s|\s$/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/**
 * Rows (arrays of strings) for the given records, header first. `opts.noun` is
 * the exporting space's word for a record. Championship scopes append explicit
 * result evidence; other spaces retain their existing columns.
 */
export function championshipRows(items, now = new Date(), opts = {}) {
  const columns = columnsFor(items, opts)
  const rows = [columns.map((c) => c.label)]
  for (const c of items) rows.push(columns.map((col) => col.get(c, now)))
  return rows
}

/** Serialize rows to CSV text (CRLF line endings — Excel's dialect). */
export function toCsv(rows) {
  return rows.map((r) => r.map(cell).join(',')).join('\r\n')
}

/** Full CSV text for a record list, headed with the space's own noun. */
export function championshipsCsv(items, now = new Date(), opts = {}) {
  return toCsv(championshipRows(items, now, opts))
}

/* ---- filename + download --------------------------------------------- */

/**
 * `srchamp-championships_2026-01-01_to_2026-03-31.csv` — range in the name.
 * `rangeLabel` overrides the derived range for exports the date range doesn't
 * describe (a hand-picked row selection is `selection-42`, not `all-dates`).
 */
export function csvFilename({ from = '', to = '', now = new Date(), prefix = 'srchamp-championships', rangeLabel = '' } = {}) {
  const a = parseYmd(from), b = parseYmd(to)
  const range =
    rangeLabel ? rangeLabel
      : a && b ? `${ymd(a)}_to_${ymd(b)}`
        : a ? `from_${ymd(a)}`
          : b ? `through_${ymd(b)}`
            : 'all-dates'
  return `${prefix}_${range}_exported-${ymd(now)}.csv`
}

/**
 * Trigger the browser download. BOM-prefixed so Excel reads UTF-8 correctly.
 * Returns false when there is no DOM (SSR / test) rather than throwing.
 */
export function downloadCsv(filename, text) {
  if (typeof document === 'undefined' || typeof URL?.createObjectURL !== 'function') return false
  const blob = new Blob(['﻿', text], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return true
}

// Export-only observation fields. Never promote school candidates into direct
// championship contacts, choose one coach arbitrarily, or infer empty from unread.
const exportContactLane = (row, lane) => row.exportContactSnapshot?.[lane] || { state: 'unread', complete: false, contacts: [] }
const contactValue = (contact, key) => {
  const namePart = value => typeof value === 'string' ? value.trim() : ''
  const fullName = namePart(contact.name) || namePart(contact.fullName) ||
    [namePart(contact.firstName ?? contact.firstname), namePart(contact.lastName ?? contact.lastname)].filter(Boolean).join(' ')
  const values = { id: contact.id ?? contact.contactId, firstName: contact.firstName ?? contact.firstname,
    lastName: contact.lastName ?? contact.lastname, name: fullName,
    role: contact.role ?? contact.title ?? contact.jobTitle ?? contact.provenance?.role ?? contact.provenance?.position, phone: contact.phone ?? contact.provenance?.phone, email: contact.email,
    companyId: contact.companyId ?? contact.provenance?.companyId, match: contact.match ?? contact.sportMatch ?? contact.provenance?.sportMatch ?? contact.provenance?.winnerMatch, source: contact.source ?? contact.provenance }
  const value = values[key]
  return value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value)
}
const indexedContactValues = (row, lane, key) => (exportContactLane(row,lane).contacts || []).map((contact,index) => `${index+1}: ${contactValue(contact,key)}`).join('\n')
export const CONTACT_EXPORT_COLUMNS = [
  ...['direct','school'].flatMap(lane => {
    const label = lane === 'direct' ? 'Championship contacts' : 'School candidates (review required)'
    return [
      {key:`${lane}_contact_state`,label:`${label}: lookup state`,get:c=>c.sourceType?'':exportContactLane(c,lane).state},
      {key:`${lane}_contact_complete`,label:`${label}: complete`,get:c=>c.sourceType?'':String(exportContactLane(c,lane).complete===true)},
      {key:`${lane}_contact_reason`,label:`${label}: reason`,get:c=>c.sourceType?'':exportContactLane(c,lane).reason || ''},
      {key:`${lane}_contact_source_kind`,label:`${label}: source kind`,get:c=>c.sourceType?'':exportContactLane(c,lane).source || ''},
      {key:`${lane}_contact_association_reason`,label:`${label}: association reason`,get:c=>c.sourceType?'':exportContactLane(c,lane).associationReason || ''},
      {key:`${lane}_contact_diagnostics`,label:`${label}: diagnostics`,get:c=>{
        if(c.sourceType)return ''
        const value=exportContactLane(c,lane)
        return value.diagnostics||value.sourceValidation?JSON.stringify({...value.diagnostics,...(value.sourceValidation?{sourceValidation:value.sourceValidation}:{})}):''
      }},
      {key:`${lane}_contact_read_at`,label:`${label}: read at (UTC)`,get:c=>c.sourceType?'':exportContactLane(c,lane).readAt || ''},
      {key:`${lane}_contact_read_contract`,label:`${label}: read contract`,get:c=>c.sourceType?'':exportContactLane(c,lane).readContract || ''},
      {key:`${lane}_contact_source_reads`,label:`${label}: source read states`,get:c=>c.sourceType?'':exportContactLane(c,lane).reads?JSON.stringify(exportContactLane(c,lane).reads):''},
      {key:`${lane}_contact_entry_diagnostics`,label:`${label}: withheld entry diagnostics`,get:c=>c.sourceType?'':exportContactLane(c,lane).entryDiagnostics?JSON.stringify(exportContactLane(c,lane).entryDiagnostics):''},
      {key:`${lane}_contact_recipient_authority`,label:`${label}: auto recipient eligible`,get:c=>c.sourceType?'':exportContactLane(c,lane).readContract?'false':''},
      ...['id','firstName','lastName','name','email','role','phone','companyId','match','source'].map(key=>({key:`${lane}_contact_${key}`,label:`${label}: ${key}`,get:c=>c.sourceType?'':indexedContactValues(c,lane,key)})),
    ]
  }),
  {key:'contact_read_at',label:'Contacts read at (UTC)',get:c=>c.sourceType?'':c.exportContactSnapshot?.readAt || ''},
  {key:'support_rep_1',label:'Recorded support rep 1',get:c=>c.sourceType?'':ownerName({rep:c.supportRep})||'Not recorded'},
  {key:'support_rep_2',label:'Recorded support rep 2',get:c=>c.sourceType?'':ownerName({rep:c.supportRep2})||'Not recorded'},
  {key:'support_read_at',label:'Recorded support read time',get:c=>c.sourceType?'':c.recordSnapshotReadAt||'Not supplied by record snapshot'},
  {key:'support_assignment_source',label:'Support assignment source',get:c=>c.sourceType?'':'Recorded sr_support_rep_1 / sr_support_rep_2; no inferred partner'},
  {key:'record_link',label:'Portal dossier link',get:c=>!c.sourceType&&c.id?`https://app.srchamp.com/command-center?record=${encodeURIComponent(String(c.id))}&act=dossier`:''},
  {key:'record_snapshot_captured_at',label:'Record snapshot captured at (UTC)',get:c=>c.exportRecordCapturedAt||''},
]

export function withExportContactSnapshot(rows,snapshot,{state='unread',reason='Current contact lookup not requested.'}={}) {
  return rows.map(row=>({...row,exportContactSnapshot:snapshot?.records?.[String(row.id)] || {
    recordId:String(row.id ?? ''),readAt:'',direct:{state,complete:false,reason,contacts:[]},school:{state:'unread',complete:false,reason,contacts:[]},
  }}))
}

// Capture every selected row before awaiting. Explicit enrichment uses bounded
// sequential batches; the read limit never truncates the exported universe.
export const exportContactBudgetMs = count => Math.min(30*60*1000,Math.max(2*60*1000,Math.max(0,Number(count)||0)*15000))
export async function prepareContactExport(rows,{readContacts,limit=100,signal,getSession=()=>0,onProgress=()=>{},lookup=false,clock=Date.now,recordCapturedAt}={}) {
  const captured=structuredClone(rows),session=getSession(),startedAt=clock(),budget=exportContactBudgetMs(rows.length)
  const captureTime = recordCapturedAt || new Date(startedAt).toISOString()
  for (const row of captured) row.exportRecordCapturedAt = captureTime
  const check=()=>{if(clock()-startedAt>=budget)throw Object.assign(new Error('Contact read budget reached. No file was generated.'),{name:'AbortError',code:'export_deadline'});if(signal?.aborted||getSession()!==session)throw Object.assign(new Error('Export canceled or signed-in session changed.'),{name:'AbortError'})}
  check()
  if(!Number.isSafeInteger(limit)||limit<1||limit>100)throw Error('Export contact batch limit must be 1 to 100.')
  const ids=[...new Set(captured.filter(row=>!row.sourceType&&/^\d{1,20}$/.test(String(row.id))).map(row=>String(row.id)))]
  if(!lookup||!ids.length)return withExportContactSnapshot(captured,null,{reason:'Current contact lookup not requested or no exact championship ID.'})
  const records={}
  for(let offset=0;offset<ids.length;offset+=limit){
    check();const batch=ids.slice(offset,offset+limit)
    let snapshot
    try{snapshot=await readContacts(batch,{signal,rows:captured,onProgress:value=>{check();onProgress({...value,completed:offset+Number(value.completed||0),total:ids.length,batch:Math.floor(offset/limit)+1,batches:Math.ceil(ids.length/limit)})}})}
    catch(error){
      check()
      for(const row of withExportContactSnapshot(batch.map(id=>({id})),null,{state:'unavailable',reason:'Contact lookup failed. This is not an empty result.'}))records[row.id]=row.exportContactSnapshot
      continue
    }
    check()
    if(snapshot?.sessionGeneration!==session)throw Object.assign(new Error('Contact snapshot does not belong to the current session.'),{name:'AbortError'})
    for(const id of batch){
      if(snapshot.records?.[id])records[id]=structuredClone(snapshot.records[id])
      else records[id]=withExportContactSnapshot([{id}],null,{state:'unavailable',reason:'Requested contact lookup returned no validated record result.'})[0].exportContactSnapshot
    }
    onProgress({phase:'batch complete',completed:offset+batch.length,total:ids.length,batch:Math.floor(offset/limit)+1,batches:Math.ceil(ids.length/limit)})
  }
  check();return withExportContactSnapshot(captured,{records})
}
